"use client";

import * as React from "react";
import { usePathname } from "next/navigation";

/** The bar itself, reusable anywhere a slim top-of-page progress indicator is
 * needed (e.g. AuthGuard's initial session check) without the route-click
 * detection that drives `TopLoader`. */
export function TopLoaderBar({ active }: { active: boolean }) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none fixed inset-x-0 top-0 z-100 h-0.75 bg-brand/20 transition-opacity duration-300 ${active ? "opacity-100" : "opacity-0"}`}
    >
      <div
        className={`h-full origin-left bg-brand shadow-[0_0_10px_var(--color-brand)] ${active ? "animate-top-loader" : "scale-x-0"}`}
      />
    </div>
  );
}

export function TopLoader() {
  const pathname = usePathname();
  const [active, setActive] = React.useState(false);
  const finished = React.useRef(true);

  // Start the bar on an internal link click that changes the route.
  React.useEffect(() => {
    function onClick(event: MouseEvent) {
      if (event.defaultPrevented || event.button !== 0) return;
      const anchor = (event.target as HTMLElement | null)?.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      const target = anchor.getAttribute("target");
      if (!href || href.startsWith("#") || target === "_blank") return;
      const isInternal =
        href.startsWith("/") || href.startsWith(window.location.origin);
      if (!isInternal) return;
      const targetPath = href
        .replace(window.location.origin, "")
        .split("?")[0]
        .split("#")[0];
      if (targetPath === pathname) return;
      finished.current = false;
      setActive(true);
    }
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [pathname]);

  // Finish when the route has actually changed.
  React.useEffect(() => {
    if (finished.current) return;
    finished.current = true;
    const timer = window.setTimeout(() => setActive(false), 450);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  return <TopLoaderBar active={active} />;
}
